import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface StripeMirrorRecord {
  id: string;
  payment_intent_id: string | null;
  charge_id: string | null;
  checkout_session_id: string | null;
  customer_id: string | null;
  transaction_date: string;
  amount: number;
  currency: string;
  stripe_status: string | null;
  processing_status: string;
  visibility_status: string;
  raw_source: string;
  stripe_object_type: string;
  stripe_event_id: string | null;
  linked_payment_id: string | null;
  linked_payment_transaction_id: string | null;
  linked_deal_id: string | null;
  linked_contact_id: string | null;
  reference_number: string | null;
  trigger_type: string | null;
  transaction_type: string | null;
  review_reason: string | null;
  created_at: string;
  updated_at: string;
  stripe_created_at: string | null;
  contact_name: string;
  deal_name: string;
  has_materialized_ledger: boolean;
  materialization_status: 'materialized' | 'unmaterialized';
}

interface UseStripeMirrorOptions {
  startDate?: string;
  endDate?: string;
  stripeStatus?: string;
  processingStatus?: string;
  materializationStatus?: string;
  linkStatus?: string;
  search?: string;
}

export function useStripeMirror({
  startDate,
  endDate,
  stripeStatus,
  processingStatus,
  materializationStatus,
  linkStatus,
  search,
}: UseStripeMirrorOptions = {}) {
  return useQuery({
    queryKey: [
      'stripe-mirror',
      startDate,
      endDate,
      stripeStatus,
      processingStatus,
      materializationStatus,
      linkStatus,
      search,
    ],
    queryFn: async () => {
      let query = (supabase.from('v_finance_stripe_mirror' as any) as any)
        .select(`
          id,
          payment_intent_id,
          charge_id,
          checkout_session_id,
          customer_id,
          transaction_date,
          amount,
          currency,
          stripe_status,
          processing_status,
          visibility_status,
          raw_source,
          stripe_object_type,
          stripe_event_id,
          linked_payment_id,
          linked_payment_transaction_id,
          linked_deal_id,
          linked_contact_id,
          reference_number,
          trigger_type,
          transaction_type,
          review_reason,
          created_at,
          updated_at,
          stripe_created_at,
          first_name,
          last_name,
          deal_name,
          has_materialized_ledger,
          materialization_status
        `)
        .order('transaction_date', { ascending: false })
        .order('created_at', { ascending: false });

      if (startDate) {
        query = query.gte('transaction_date', startDate);
      }
      if (endDate) {
        query = query.lte('transaction_date', endDate);
      }
      if (stripeStatus && stripeStatus !== 'all') {
        query = query.eq('stripe_status', stripeStatus);
      }
      if (processingStatus && processingStatus !== 'all') {
        query = query.eq('processing_status', processingStatus);
      }
      if (materializationStatus && materializationStatus !== 'all') {
        query = query.eq('materialization_status', materializationStatus);
      }
      if (linkStatus === 'linked') {
        query = query.not('linked_payment_transaction_id', 'is', null);
      }
      if (linkStatus === 'unlinked') {
        query = query.is('linked_payment_transaction_id', null);
      }

      const { data, error } = await query.limit(500);
      if (error) throw error;

      const normalizedSearch = search?.trim().toLowerCase();

      return ((data as any[]) || [])
        .map((row): StripeMirrorRecord => ({
          id: row.id,
          payment_intent_id: row.payment_intent_id ?? null,
          charge_id: row.charge_id ?? null,
          checkout_session_id: row.checkout_session_id ?? null,
          customer_id: row.customer_id ?? null,
          transaction_date: row.transaction_date,
          amount: row.amount ?? 0,
          currency: row.currency || 'USD',
          stripe_status: row.stripe_status ?? null,
          processing_status: row.processing_status,
          visibility_status: row.visibility_status,
          raw_source: row.raw_source,
          stripe_object_type: row.stripe_object_type,
          stripe_event_id: row.stripe_event_id ?? null,
          linked_payment_id: row.linked_payment_id ?? null,
          linked_payment_transaction_id: row.linked_payment_transaction_id ?? null,
          linked_deal_id: row.linked_deal_id ?? null,
          linked_contact_id: row.linked_contact_id ?? null,
          reference_number: row.reference_number ?? null,
          trigger_type: row.trigger_type ?? null,
          transaction_type: row.transaction_type ?? null,
          review_reason: row.review_reason ?? null,
          created_at: row.created_at,
          updated_at: row.updated_at,
          stripe_created_at: row.stripe_created_at ?? null,
          contact_name: `${row.first_name || ''} ${row.last_name || ''}`.trim() || '-',
          deal_name: row.deal_name || '-',
          has_materialized_ledger: Boolean(row.has_materialized_ledger),
          materialization_status: row.materialization_status === 'materialized' ? 'materialized' : 'unmaterialized',
        }))
        .filter((row) => {
          if (!normalizedSearch) return true;
          const haystack = [
            row.contact_name,
            row.deal_name,
            row.customer_id,
            row.payment_intent_id,
            row.charge_id,
            row.reference_number,
            row.linked_payment_id,
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          return haystack.includes(normalizedSearch);
        });
    },
  });
}
